// Client-side wrapper around /api/wx/winds and /api/wx/winds-profile.
//
// Provides three things:
//   1. fetchWinds(lat, lon, alt, time?)  - single-point single-altitude lookup
//   2. fetchProfile(lat, lon, time?, hours?) - full vertical profile + time window
//                                              for the in-app flight wx briefing
//   3. sampleWindsAlongRoute(route, alt, time?) - one wind per leg, sampled at
//                                                  the leg midpoint, suitable
//                                                  to feed straight into
//                                                  calcFlight()'s `winds[]`.
//
// All three use a tiny LRU cache keyed on (rounded lat/lon, alt, hour-bucket)
// to dedupe concurrent calls from PlanPanel + FlightsPage + the ruler.

import { greatCircleNm } from './geo';
import type { WindAloft } from './flightCalc';

export interface WindsAtPoint {
  lat: number;
  lon: number;
  altFt: number;
  /** ISO 8601 UTC. */
  validTime: string;
  dirFromTrue: number;
  speedKt: number;
  tempC: number | null;
  pressureHpa: number;
  source: string;
  interpolation: {
    fromHpa: number;
    toHpa: number;
    fraction: number;
    fromAltFt: number;
    toAltFt: number;
  };
}

export interface ProfileLevel {
  hpa: number;
  altFt: number;
  dirFromTrue: number;
  speedKt: number;
  tempC: number | null;
}

export interface ProfileHour {
  validTime: string;
  levels: ProfileLevel[];
}

export interface WindsProfile {
  lat: number;
  lon: number;
  source: string;
  hours: ProfileHour[];
}

// ---------- LRU cache ------------------------------------------------------

import { kvGet, kvSet } from './idb';

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

const TTL_MS = 30 * 60 * 1000; // 30 minutes
const MAX_ENTRIES = 256;
const cache = new Map<string, CacheEntry<WindsAtPoint | WindsProfile>>();

// IDB persistence: rehydrate winds cache on module load (OAC6).
// Allows flight plan ETE calculations to have wind data after tab eviction.
const IDB_WINDS_KEY = 'fyrefly:winds:cache:v1';
// Limit IDB snapshot size to avoid storing 256 full WindsProfile objects.
const IDB_MAX_ENTRIES = 50;

kvGet<Array<[string, CacheEntry<WindsAtPoint | WindsProfile>]>>(IDB_WINDS_KEY)
  .then((stored) => {
    if (!Array.isArray(stored)) return;
    const now = Date.now();
    for (const [k, entry] of stored) {
      if (entry.expiresAt > now && !cache.has(k)) {
        cache.set(k, entry);
      }
    }
  })
  .catch(() => { /* non-fatal */ });

function persistWindsCache(): void {
  // Persist the most-recently-used entries (last IDB_MAX_ENTRIES).
  const entries = [...cache.entries()].slice(-IDB_MAX_ENTRIES);
  kvSet(IDB_WINDS_KEY, entries).catch(() => { /* non-fatal */ });
}

function cacheGet<T>(key: string): T | null {
  const e = cache.get(key);
  if (!e) return null;
  if (e.expiresAt < Date.now()) {
    cache.delete(key);
    return null;
  }
  // Refresh recency.
  cache.delete(key);
  cache.set(key, e);
  return e.value as T;
}

function cacheSet<T extends WindsAtPoint | WindsProfile>(key: string, value: T): void {
  if (cache.size >= MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest) cache.delete(oldest);
  }
  cache.set(key, { value, expiresAt: Date.now() + TTL_MS });
  // Persist to IDB for tab-eviction recovery (debounced to avoid
  // hammering IDB on every winds fetch during flight).
  persistWindsCache();
}

// ---------- public API -----------------------------------------------------

/**
 * Snap a Date or ISO string to the nearest top-of-hour, returned as ISO 8601 UTC.
 * The /api/wx endpoints round to nearest hour anyway, so we use this for cache keys.
 */
function hourBucket(time: Date | string | undefined | null): string {
  let d: Date;
  if (time == null) {
    d = new Date(Date.now() + 30 * 60 * 1000);
  } else if (time instanceof Date) {
    d = new Date(time.valueOf());
  } else {
    d = new Date(time);
  }
  if (Number.isNaN(d.valueOf())) d = new Date(Date.now() + 30 * 60 * 1000);
  d.setUTCMinutes(0, 0, 0);
  return d.toISOString();
}

function roundQuarter(n: number): number {
  // 0.25 deg matches GFS native grid AND the upstream cache key the
  // /api/wx endpoints use, so client + server caches collapse together.
  return Math.round(n * 4) / 4;
}

export async function fetchWinds(
  lat: number,
  lon: number,
  altFt: number,
  time?: Date | string | null,
  signal?: AbortSignal,
): Promise<WindsAtPoint | null> {
  const cacheLat = roundQuarter(lat);
  const cacheLon = roundQuarter(lon);
  const altRounded = Math.round(altFt / 500) * 500; // 500ft buckets
  const timeKey = hourBucket(time);
  const key = `winds:${cacheLat},${cacheLon},${altRounded},${timeKey}`;
  const hit = cacheGet<WindsAtPoint>(key);
  if (hit) return hit;

  const params = new URLSearchParams({
    lat: String(cacheLat),
    lon: String(cacheLon),
    alt: String(altFt),
  });
  if (time) params.set('time', new Date(time).toISOString());

  try {
    const r = await fetch(`/api/wx/winds?${params}`, { signal });
    if (!r.ok) return null;
    const body = (await r.json()) as WindsAtPoint;
    cacheSet(key, body);
    return body;
  } catch (e) {
    // AbortError is expected during rapid plan edits; everything else
    // we let bubble up as null and the consumer falls back to no-wind.
    if ((e as { name?: string })?.name === 'AbortError') return null;
    // eslint-disable-next-line no-console
    console.warn('[winds] fetch failed', e);
    return null;
  }
}

export async function fetchProfile(
  lat: number,
  lon: number,
  time?: Date | string | null,
  hours: number = 12,
  signal?: AbortSignal,
): Promise<WindsProfile | null> {
  const cacheLat = roundQuarter(lat);
  const cacheLon = roundQuarter(lon);
  const timeKey = hourBucket(time);
  const key = `profile:${cacheLat},${cacheLon},${hours}h,${timeKey}`;
  const hit = cacheGet<WindsProfile>(key);
  if (hit) return hit;

  const params = new URLSearchParams({
    lat: String(cacheLat),
    lon: String(cacheLon),
    hours: String(hours),
  });
  if (time) params.set('time', new Date(time).toISOString());

  try {
    const r = await fetch(`/api/wx/winds-profile?${params}`, { signal });
    if (!r.ok) return null;
    const body = (await r.json()) as WindsProfile;
    cacheSet(key, body);
    return body;
  } catch (e) {
    if ((e as { name?: string })?.name === 'AbortError') return null;
    // eslint-disable-next-line no-console
    console.warn('[winds] profile fetch failed', e);
    return null;
  }
}

/**
 * Sample winds at the midpoint of each leg of `route`, in parallel,
 * shaped to feed straight into calcFlight()'s `winds: WindAloft[]`.
 *
 * Returns an array of length route.length-1; entries are null where the
 * fetch failed (calcFlight degrades to no-wind on that leg).
 *
 * Long legs (>100nm) get sampled at multiple points and the wind is
 * vector-averaged \u2014 same approach ForeFlight uses to handle the case
 * where a SFO\u2192LAX leg crosses a coastal trough that flips wind direction
 * mid-route.
 */
export async function sampleWindsAlongRoute(
  route: { lat: number; lon: number }[],
  altFt: number,
  time?: Date | string | null,
): Promise<(WindAloft | null)[]> {
  if (route.length < 2) return [];
  const out: Promise<WindAloft | null>[] = [];

  for (let i = 1; i < route.length; i++) {
    const a = route[i - 1];
    const b = route[i];
    const distNm = greatCircleNm(a.lat, a.lon, b.lat, b.lon);

    // For long legs, sample 3 points along the great circle and
    // vector-average. For short legs, one midpoint sample is enough
    // and saves us 2 round-trips.
    const samples: { lat: number; lon: number }[] = distNm > 100
      ? [
          interpGc(a, b, 0.25),
          interpGc(a, b, 0.50),
          interpGc(a, b, 0.75),
        ]
      : [interpGc(a, b, 0.50)];

    out.push((async () => {
      const winds = await Promise.all(
        samples.map((p) => fetchWinds(p.lat, p.lon, altFt, time))
      );
      const valid = winds.filter((w): w is WindsAtPoint => w !== null);
      if (valid.length === 0) return null;
      // Vector-average the wind across samples.
      let u = 0, v = 0;
      for (const w of valid) {
        const wt = (w.dirFromTrue + 180) % 360; // wind-to direction
        const rad = (wt * Math.PI) / 180;
        u += w.speedKt * Math.sin(rad);
        v += w.speedKt * Math.cos(rad);
      }
      u /= valid.length;
      v /= valid.length;
      const speedKt = Math.hypot(u, v);
      const dirToTrue = (Math.atan2(u, v) * 180 / Math.PI + 360) % 360;
      const dirFromTrue = (dirToTrue + 180) % 360;
      return { dirFromTrue, speedKt };
    })());
  }

  return Promise.all(out);
}

/**
 * Linearly interpolate along a great-circle path. Good enough for sampling
 * winds along legs <500nm; for longer legs we'd want spherical great-circle
 * (Haversine intermediate-point math), but no current Fyrefly leg approaches
 * that length.
 */
function interpGc(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
  f: number,
): { lat: number; lon: number } {
  return {
    lat: a.lat + (b.lat - a.lat) * f,
    lon: a.lon + (b.lon - a.lon) * f,
  };
}
